import {css, StyleSheet} from 'aphrodite';
import React, {
  MouseEventHandler,
  RefCallback,
  RefObject,
  SVGAttributes,
  useMemo,
  useRef,
  useState,
} from 'react';

import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import {
  PlateMapWellDisplayLayer,
  AssignmentGroup,
  AssignmentGroupValue,
  PlateMapDisplayLayerAttribute,
  PlateMap,
  SourceErrorsBySourceDilutionId,
  DEFAULT_SOURCE_ERRORS_BY_SOURCE_DILUTION_ID,
} from 'samples/plate-maps/PlateMapConstants';
import {
  getDisplayPlateMap,
  getWellIndicesBetweenPositions,
  getWellPositionFromIndex,
} from 'samples/plate-maps/PlateMapUtil';
import PlateMapWellView from 'samples/plate-maps/PlateMapWellView';
import {ValidationErrorsByLayerAndLevel} from 'samples/plate-maps/validation/ValidationTypes';
import {formatGridColumnCoordinate, formatGridRowCoordinate} from 'samples/SampleUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_LIGHT, GRAY_LIGHTER} from 'ui-library/Colors';
import useOnMount from 'ui-library/hooks/useOnMount';

const LABEL_MARGIN = 24;
const PLATE_PADDING = 12;
const WELL_SIZE = 28;
const MAX_CELL_SIZE = 150;
const labelProps = {dominantBaseline: 'central', textAnchor: 'middle'};

/**
 * Renders a row/column label on a plate map with a larger hitbox for mouse interactions.
 */
function PlateMapDimensionLabel({
  x,
  y,
  label,
  hitboxWidth,
  hitboxHeight,
  onMouseEnter,
  onClick,
  isHovered,
}: {
  x: number;
  y: number;
  label: string | number;
  hitboxWidth: number;
  hitboxHeight: number;
  onMouseEnter: MouseEventHandler;
  onClick: MouseEventHandler;
  isHovered: boolean;
}): JSX.Element {
  return (
    <g transform={`translate(${x}, ${y})`} onMouseEnter={onMouseEnter} onClick={onClick}>
      <rect
        x={-hitboxWidth / 2}
        y={-hitboxHeight / 2}
        width={hitboxWidth}
        height={hitboxHeight}
        fill={isHovered ? GRAY_LIGHT : 'none'}
        pointerEvents="all"
      />
      <text className={css(styles.text)} x={0} y={0} {...labelProps}>
        {label}
      </text>
    </g>
  );
}

export interface PlateMapViewProps {
  plateMap: PlateMap;
  selectedWellIndices: Set<number>;
  setSelectedWellIndices: (indices: Set<number>) => void;
  assignmentGroupsById: {[id: string]: AssignmentGroup<AssignmentGroupValue>};
  isViewOnly: boolean;
  displayLayer: PlateMapWellDisplayLayer;
  previewedWellIndices: Set<number>;
  attribute: PlateMapDisplayLayerAttribute;
  /**
   * List of refs which should deselect selected wells on click. Note that only the element specified, and
   * not any children, will trigger the deselection.
   */
  deselectWellsOnClickRefs?: Array<RefObject<HTMLElement>>;
  /**
   * A ref to the modal containing this plate map. This is used to deselect wells on clicks on certain parts
   * of that modal.
   */
  containingModalRef?: RefObject<HTMLDivElement> | null;
  maxHeight?: string;
  svgRef?: RefCallback<Element> | null;
  /**
   * Whether to show current concentrations and quantities in well popovers.
   * During a transfer flow, we want to display final/target values, but on the inventory plate page for an
   * existing plate, we want to display the current values.
   */
  showFinalMeasurements: boolean;
  groupedValidationErrors?: ValidationErrorsByLayerAndLevel;
  /** Type of the plate map; used to render slightly different errors/warnings for designs. */
  plateMapType?: PlateMapType | null;
  /** Error messages for previewed sources to render in the plate map. */
  previewedSourceErrorsBySourceDilutionId?: SourceErrorsBySourceDilutionId;
  /** Whether to derive well opacity from finalConcentration values in the reagent display layer. */
  applyConcentrationOpacity?: boolean;
}

export default function PlateMapView({
  plateMap,
  isViewOnly,
  selectedWellIndices,
  setSelectedWellIndices,
  assignmentGroupsById,
  displayLayer,
  previewedWellIndices,
  attribute,
  deselectWellsOnClickRefs = [],
  containingModalRef = null,
  maxHeight = 'none',
  svgRef = null,
  showFinalMeasurements,
  groupedValidationErrors,
  plateMapType,
  previewedSourceErrorsBySourceDilutionId = DEFAULT_SOURCE_ERRORS_BY_SOURCE_DILUTION_ID,
  applyConcentrationOpacity = false,
}: PlateMapViewProps): JSX.Element {
  const {size} = plateMap;
  const plateWidthMargin = LABEL_MARGIN + PLATE_PADDING * 2;
  const wellPadding = size.numRows > 12 ? 1 : 4;
  const cellSize = WELL_SIZE + wellPadding * 2;

  const plateBorderWidth = 1;
  const widthPx = cellSize * size.numColumns + plateWidthMargin - plateBorderWidth;
  const maxWidthPx = MAX_CELL_SIZE * size.numColumns + plateWidthMargin;
  const heightPx = plateWidthMargin + cellSize * size.numRows;

  const [hoveredWellIndices, setHoveredWellIndices] = useState<Set<number>>(new Set());
  const [hoveredRowOrColumn, setHoveredRowOrColumn] = useState<string | null>(null);
  const [dragStartIndex, setDragStartIndex] = useState<number | null>(null);
  // Treat the start of a shift multi-select as the last well that was clicked via any action
  const [shiftSelectStartIndex, setShiftSelectStartIndex] = useState<number | null>(null);
  const borderRef = useRef<SVGRectElement | null>(null);

  useOnMount(() => {
    function resetWellSelectionOnClickOut(e: MouseEvent): void {
      if ([borderRef, ...deselectWellsOnClickRefs].some((ref) => ref.current === e.target)) {
        setSelectedWellIndices(new Set());
      } else if (
        // If the plate map is in a modal, certain parts of that modal should deselect wells. This depends on
        // the current modal structure.
        containingModalRef?.current != null &&
        (containingModalRef.current === e.target ||
          Array.from(
            containingModalRef.current.querySelectorAll(
              '.modal-dialog, .modal-title, .modal-header, .modal-body, .modal-footer, .modal-footer > form'
            )
          ).some((node) => node === e.target))
      ) {
        setSelectedWellIndices(new Set());
      }
    }
    window.addEventListener('click', resetWellSelectionOnClickOut);
    return () => {
      window.removeEventListener('click', resetWellSelectionOnClickOut);
    };
  });

  // To prevent unnecessary re-renders when wells are not previewed, we don't use the passed in set (which
  // will change each time a well is selected) and use null instead. That way, `getDisplayPlateMap` will
  // not be re-called each time a well is selected.
  // TODO(BNCH-117706): Fix the case where wells are previewed. In that case, every well is re-rendered each
  // time a well is selected.
  const memoizedPreviewedWellIndices = useMemo(
    () => (previewedWellIndices.size === 0 ? null : previewedWellIndices),
    [previewedWellIndices]
  );

  const displayPlateMap = useMemo(
    () =>
      getDisplayPlateMap(
        {
          displayLayer,
          plateMap,
          assignmentGroupsById,
          attribute,
          groupedValidationErrors: groupedValidationErrors ?? {},
        },
        memoizedPreviewedWellIndices ?? new Set(),
        previewedSourceErrorsBySourceDilutionId,
        plateMapType,
        applyConcentrationOpacity
      ),
    [
      assignmentGroupsById,
      displayLayer,
      plateMap,
      attribute,
      groupedValidationErrors,
      memoizedPreviewedWellIndices,
      previewedSourceErrorsBySourceDilutionId,
      plateMapType,
      applyConcentrationOpacity,
    ]
  );

  const dataParameterNamesByVersionId = useMemo(
    () => plateMap.dataParameterNamesByVersionId ?? {},
    [plateMap.dataParameterNamesByVersionId]
  );
  const testOrderDilutionInfoById = useMemo(
    () => plateMap.testOrderDilutionInfoById ?? {},
    [plateMap.testOrderDilutionInfoById]
  );
  const concentrationLayerInfoById = useMemo(
    () => plateMap.concentrationLayerInfoById ?? {},
    [plateMap.concentrationLayerInfoById]
  );
  const metadataLayerInfoById = useMemo(
    () => plateMap.metadataLayerInfoById ?? {},
    [plateMap.metadataLayerInfoById]
  );

  function clearHoveredWells(): void {
    setDragStartIndex(null);
    setHoveredWellIndices(new Set());
    setHoveredRowOrColumn(null);
  }

  function toggleWellSelection(wellIndices: Set<number>): void {
    // If all the given wells are already selected, deselect them all.
    if ([...wellIndices].every((i) => selectedWellIndices.has(i))) {
      const newSelectedWells = [...selectedWellIndices].filter((i) => !wellIndices.has(i));
      setSelectedWellIndices(new Set(newSelectedWells));
    } else {
      // Otherwise add the given wells to the selected wells.
      setSelectedWellIndices(new Set([...selectedWellIndices, ...wellIndices]));
    }
  }

  function selectHoveredWells(): void {
    toggleWellSelection(hoveredWellIndices);
  }

  function handleWellMouseDown(index: number): void {
    if (!isViewOnly) {
      setDragStartIndex(index);
    }
  }

  function handleWellMouseUp(index: number, isShift: boolean): void {
    if (!isViewOnly) {
      // If shift is held and user is not currently making a drag selection, treat as a shift multi-select
      if (isShift && (dragStartIndex == null || dragStartIndex === index) && shiftSelectStartIndex != null) {
        const startPosn = getWellPositionFromIndex(shiftSelectStartIndex, size);
        const endPosn = getWellPositionFromIndex(index, size);
        const wellsToSelect = getWellIndicesBetweenPositions(startPosn, endPosn, size);
        toggleWellSelection(new Set(wellsToSelect));
        setDragStartIndex(null);
      } else if (dragStartIndex != null) {
        setDragStartIndex(null);
        selectHoveredWells();
        setHoveredWellIndices(new Set([index]));
      }
      setShiftSelectStartIndex(index);
    }
  }

  function handleMouseEnter(index: number): void {
    if (isViewOnly) {
      return;
    }
    setHoveredRowOrColumn(null);
    if (dragStartIndex == null) {
      setHoveredWellIndices(new Set([index]));
    } else {
      const startPosn = getWellPositionFromIndex(dragStartIndex, size);
      const endPosn = getWellPositionFromIndex(index, size);
      const wellsToHover = getWellIndicesBetweenPositions(startPosn, endPosn, size);
      setHoveredWellIndices(new Set(wellsToHover));
    }
  }

  function onHoverRow(index: number): void {
    if (!isViewOnly) {
      const wellsToHover = getWellIndicesBetweenPositions(
        {row: index, column: 0},
        {row: index, column: size.numColumns - 1},
        size
      );
      setHoveredWellIndices(new Set(wellsToHover));
      setHoveredRowOrColumn(`r${index}`);
    }
  }

  function onHoverColumn(index: number): void {
    if (!isViewOnly) {
      const wellsToHover = getWellIndicesBetweenPositions(
        {row: 0, column: index},
        {row: size.numRows - 1, column: index},
        size
      );
      setHoveredWellIndices(new Set(wellsToHover));
      setHoveredRowOrColumn(`c${index}`);
    }
  }

  const strokeProps = useMemo(() => {
    const stroke: SVGAttributes<SVGRectElement> = {
      strokeWidth: plateBorderWidth,
      stroke: '#979799',
    };
    if (plateMapType === PlateMapType.DESIGN) {
      stroke.strokeDasharray = 5;
    }
    return stroke;
  }, [plateMapType, plateBorderWidth]);

  // TODO(BNCH-75467): Remove the duplicated styles which are used in temporary-aphrodite-styles.less
  return (
    <svg
      className="plate-map"
      ref={svgRef}
      viewBox={`0 0 ${widthPx} ${heightPx}`}
      style={{minWidth: `${widthPx}px`, maxWidth: `${maxWidthPx}px`, minHeight: `${heightPx}px`, maxHeight}}
      cursor={isViewOnly ? 'auto' : 'pointer'}
      {...getTestProps({component: 'PlateMapView', element: 'root'})}
    >
      <rect
        x="0"
        y="0"
        width={widthPx}
        height={heightPx}
        fill={GRAY_LIGHTER}
        onMouseLeave={clearHoveredWells}
        ref={borderRef}
        {...strokeProps}
      />
      <g transform={`translate(${LABEL_MARGIN + PLATE_PADDING}, ${PLATE_PADDING + LABEL_MARGIN / 2})`}>
        {[...Array(size.numColumns)].map((_, columnIndex) => (
          <PlateMapDimensionLabel
            key={`c${columnIndex}`}
            x={cellSize * (columnIndex + 0.5)}
            y={0}
            label={formatGridColumnCoordinate(columnIndex)}
            hitboxWidth={cellSize}
            hitboxHeight={LABEL_MARGIN}
            onMouseEnter={() => {
              onHoverColumn(columnIndex);
            }}
            onClick={selectHoveredWells}
            isHovered={hoveredRowOrColumn === `c${columnIndex}`}
          />
        ))}
      </g>
      <g transform={`translate(${PLATE_PADDING + LABEL_MARGIN / 2}, ${LABEL_MARGIN + PLATE_PADDING})`}>
        {[...Array(size.numRows)].map((_, rowIndex) => (
          <PlateMapDimensionLabel
            key={`r${rowIndex}`}
            x={0}
            y={cellSize * (rowIndex + 0.5)}
            label={formatGridRowCoordinate(rowIndex)}
            hitboxWidth={LABEL_MARGIN}
            hitboxHeight={cellSize}
            onMouseEnter={() => {
              onHoverRow(rowIndex);
            }}
            onClick={selectHoveredWells}
            isHovered={hoveredRowOrColumn === `r${rowIndex}`}
          />
        ))}
      </g>

      <g transform={`translate(${LABEL_MARGIN + PLATE_PADDING},${LABEL_MARGIN + PLATE_PADDING})`}>
        {displayPlateMap.wells.map((well, index) => (
          <g
            onMouseDown={(e) => {
              // Prevent default so that text on the svg doesn't get highlighted when dragging
              e.preventDefault();
              handleWellMouseDown(index);
            }}
            onMouseUp={(e) => {
              handleWellMouseUp(index, e.shiftKey);
            }}
            onMouseEnter={() => {
              handleMouseEnter(index);
            }}
            {...getTestProps({component: 'PlateMapWellView', element: 'root'})}
            key={index}
          >
            <PlateMapWellView
              cellSize={cellSize}
              wellSize={WELL_SIZE}
              isHovered={!isViewOnly && hoveredWellIndices.has(index)}
              isSelected={!isViewOnly && selectedWellIndices.has(index)}
              isPreview={!isViewOnly && (memoizedPreviewedWellIndices?.has(index) ?? false)}
              well={well}
              displayLayer={displayLayer}
              enablePopovers={
                // 1. Always enable popovers in view-only mode
                // 2. When not in view-only mode:
                //    a. Disable popovers when clicking and dragging, so they don't get in the way
                //    b. When not clicking and dragging, we would like it to behave as if popovers are enabled
                //       on all cells. However, because of (2a), any click (initiating a drag) would change
                //       the value of the `enablePopovers` prop on every cell, forcing them all to re-render.
                //       To avoid these spurious renders, we only enable popovers on the currently hovered
                //       cell.
                //    c. There's a problem with enabling popovers only on the currently hovered cell though.
                //       The popover appears north of the hovered cell. If you try to move your mouse pointer
                //       into the popover, it leaves the hovered cell, because there is a space between the
                //       edge of the cell and the edge of the popover. The moment the pointer leaves the cell,
                //       it disables popovers on that cell, immediately closing the open popover. To fix this
                //       issue, we also need to enable popovers on the cell immediately south of the hovered
                //       cell. This way, when you move the mouse north to the popover, the cell you just left
                //       still keeps its popovers enabled, and the popover stays open long enough for your
                //       mouse pointer to get there.
                //    d. For reasons I haven't really investigated, the cell immediately north of the
                //       currently hovered cell also needs to have popovers enabled. It could be that the
                //       popover system closes any open popover as soon as the mouse hovers over an element
                //       with popovers disabled, but that's just a guess. In any case, experimentation has
                //       shown this approach to work. Edit: in addition, the popover can sometimes appear
                //       south of the hovered cell, when there isn't enough real estate to the north.
                isViewOnly ||
                (dragStartIndex == null &&
                  (hoveredWellIndices.has(index) ||
                    hoveredWellIndices.has(index + size.numColumns) ||
                    hoveredWellIndices.has(index - size.numColumns)))
              }
              dataParameterNamesByVersionId={dataParameterNamesByVersionId}
              testOrderDilutionInfoById={testOrderDilutionInfoById}
              concentrationLayerInfoById={concentrationLayerInfoById}
              metadataLayerInfoById={metadataLayerInfoById}
              showFinalMeasurements={showFinalMeasurements}
            />
          </g>
        ))}
      </g>
    </svg>
  );
}

const styles = StyleSheet.create({
  text: {cursor: 'pointer'},
});
